import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { createUser, updateUser } from "../../api/usersService";
import { uploadAvatar } from "../../api/avatarService";
import { addInstructor, fetchInstructors } from "../../store/instructorsSlice";
import { addCar, fetchCars, removeCar, updateCar } from "../../store/carsSlice";
import { fetchUsers } from "../../store/usersSlice";
import { UserContext } from "../../UserContext";
import { closePopup as closePopupUI } from "../Common/popupUIStore";
import DistrictsDatalist from "../Common/DistrictsDatalist";
import {
   DEFAULT_INSTRUCTOR_TYPE,
   getStaffTitle,
   INSTRUCTOR_TYPE_OPTIONS,
   instructorTypeHasCar,
} from "../../constants/instructors";
import { rememberAvatarChange } from "../../utils/userAvatar";
import { ButtonUI, ChoiceUI, InputUI } from "../UI";
import StaffProfileHead from "./StaffProfile/StaffProfileHead";
import { GEARBOX_OPTIONS } from "./InstructorProfile/InstructorMainTab";
import {
   PROFILE_COLOR_PALETTE,
   DEFAULT_COLOR_INDEX,
   colorIndexToCss,
} from "./InstructorProfile/instructorProfileShared";

// culorile conturilor de manager / admin (aceleași ca în profilul lor, StaffProfileUI)
const ACCOUNT_COLOR_TOKENS = ["--red", "--orange", "--yellow", "--green", "--blue", "--indigo", "--purple", "--pink"];

const ALL_ROLE_OPTIONS = [
   {
      value: "INSTRUCTOR",
      label: "Instructor",
   },
   {
      value: "ADMIN",
      label: "Admin",
   },
   {
      value: "MANAGER",
      label: "Manager",
   },
   {
      value: "PROFESSOR",
      label: "Profesor",
   },
];

function normalizeRole(value, options) {
   const role = String(value || "").trim().toUpperCase();
   return options.some((option) => option.value === role) ? role : null;
}

function getDefaultRole(initialRole, options) {
   return normalizeRole(initialRole, options) || options[0]?.value || "INSTRUCTOR";
}

function getRoleLabel(role, options) {
   return options.find((option) => option.value === role)?.label || "Personal";
}

function buildInitialForm() {
   return {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      password: "",
      confirmPassword: "",
      sector: "",
      carPlate: "",
      gearbox: "manual",
      instructorType: DEFAULT_INSTRUCTOR_TYPE,
      colorIndex: DEFAULT_COLOR_INDEX,
      colorToken: "--blue",
   };
}

function normPlate(value) {
   return String(value || "")
      .replace(/\s+/g, "")
      .replace(/-/g, "")
      .toUpperCase();
}

function toApiGearbox(value) {
   return String(value || "")
      .toLowerCase()
      .includes("auto")
      ? "automat"
      : "manual";
}

export default function PersonalCreateUI({ initialRole = null }) {
   const dispatch = useDispatch();
   const { user } = useContext(UserContext);
   const { list: instructors = [], status: instructorsStatus = "idle" } =
      useSelector((state) => state.instructors || {});
   const { list: cars = [], status: carsStatus = "idle" } = useSelector(
      (state) => state.cars || {},
   );

   const roleOptions = useMemo(() => {
      const isAdmin =
         String(user?.role || "")
            .trim()
            .toUpperCase() === "ADMIN";

      if (isAdmin) return ALL_ROLE_OPTIONS;

      return ALL_ROLE_OPTIONS.filter((option) => option.value !== "ADMIN");
   }, [user?.role]);

   const selectedRole = useMemo(
      () => getDefaultRole(initialRole, roleOptions),
      [initialRole, roleOptions],
   );

   const selectedRoleLabel = getRoleLabel(selectedRole, roleOptions);
   const [form, setForm] = useState(() => buildInitialForm());
   const [submitting, setSubmitting] = useState(false);
   const [error, setError] = useState("");

   useEffect(() => {
      if (selectedRole !== "INSTRUCTOR") return;
      if (instructorsStatus === "idle") dispatch(fetchInstructors());
      if (carsStatus === "idle") dispatch(fetchCars());
   }, [carsStatus, dispatch, instructorsStatus, selectedRole]);

   const instructorsById = useMemo(() => {
      const map = new Map();

      for (const item of instructors || []) {
         const id = String(item?.id ?? "").trim();
         if (!id) continue;
         map.set(id, item);
      }

      return map;
   }, [instructors]);

   const carsByPlate = useMemo(() => {
      const map = new Map();

      for (const car of cars || []) {
         const plate = normPlate(car?.plateNumber);
         if (!plate) continue;
         map.set(plate, car);
      }

      return map;
   }, [cars]);

   const carsByInstructorId = useMemo(() => {
      const map = new Map();

      for (const car of cars || []) {
         const id = String(car?.instructorId ?? "").trim();
         if (!id) continue;
         map.set(id, car);
      }

      return map;
   }, [cars]);

   const normalizedCarPlate = useMemo(
      () => normPlate(form.carPlate),
      [form.carPlate],
   );

   const duplicateCar = useMemo(() => {
      if (selectedRole !== "INSTRUCTOR" || !normalizedCarPlate) return null;
      return carsByPlate.get(normalizedCarPlate) || null;
   }, [carsByPlate, normalizedCarPlate, selectedRole]);

   const duplicateOwner = useMemo(() => {
      const ownerId = String(duplicateCar?.instructorId ?? "").trim();
      if (!ownerId) return null;
      return instructorsById.get(ownerId) || null;
   }, [duplicateCar, instructorsById]);

   const duplicateOwnerLabel = useMemo(() => {
      if (!duplicateOwner) return "";

      const fullName =
         `${duplicateOwner.firstName || ""} ${
            duplicateOwner.lastName || ""
         }`.trim();

      if (fullName) return fullName;
      return `Instructor #${duplicateOwner.id}`;
   }, [duplicateOwner]);

   const updateFormValue = (field, value) => {
      setForm((current) => ({
         ...current,
         [field]: value,
      }));
   };

   const upsertCarForInstructor = async ({ instructorId, plate, gearbox }) => {
      const normalizedPlate = normPlate(plate);
      const existing = carsByInstructorId.get(String(instructorId));

      if (!normalizedPlate) {
         if (existing) {
            await dispatch(removeCar(existing.id)).unwrap();
         }
         return;
      }

      const duplicate = carsByPlate.get(normalizedPlate);

      if (duplicate && String(duplicate?.instructorId ?? "") !== String(instructorId)) {
         // mașina trece la acest instructor; mașina lui de până acum o șterge serverul
         await dispatch(
            updateCar({
               id: duplicate.id,
               plateNumber: duplicate.plateNumber,
               instructorId,
               gearbox: toApiGearbox(gearbox),
            }),
         ).unwrap();

         return;
      }

      const payload = {
         plateNumber: String(plate || "").trim(),
         instructorId,
         gearbox: toApiGearbox(gearbox),
      };

      if (existing) {
         await dispatch(updateCar({ id: existing.id, ...payload })).unwrap();
      } else {
         await dispatch(addCar(payload)).unwrap();
      }
   };

   const validateForm = () => {
      if (!form.firstName.trim() || !form.lastName.trim()) {
         return "Completează numele și prenumele.";
      }
      if (!form.email.trim()) {
         return "Email este obligatoriu.";
      }
      if (!form.password || form.password.length < 6) {
         return "Parola trebuie să aibă minim 6 caractere.";
      }
      if (form.password !== form.confirmPassword) {
         return "Parolele nu coincid.";
      }
      return "";
   };

   const isInstructor = selectedRole === "INSTRUCTOR";
   const hasCar = isInstructor && instructorTypeHasCar(form.instructorType);
   const supportsAccountColor = selectedRole === "MANAGER" || selectedRole === "ADMIN";
   const viewerRole = String(user?.role || "").trim().toUpperCase();
   // fotografia se poate pune doar unde o permite serverul: adminul oricui din personal, managerul instructorilor
   const canSetPhoto = viewerRole === "ADMIN" || (viewerRole === "MANAGER" && isInstructor);
   const headColor = isInstructor
      ? PROFILE_COLOR_PALETTE[form.colorIndex]?.visual
      : supportsAccountColor
        ? `var(--event-${form.colorToken.slice(2)}, var(--event-default))`
        : undefined;

   // fotografia aleasă înainte de creare: se vede în antet, se încarcă după ce există contul
   const [photo, setPhoto] = useState({ blob: null, previewUrl: "" });
   useEffect(
      () => () => {
         if (photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
      },
      [photo.previewUrl],
   );

   const handleChange = (event) => updateFormValue(event.target.name, event.target.value);

   const handleSubmit = async (event) => {
      event.preventDefault();

      const validationError = validateForm();
      if (validationError) {
         setError(validationError);
         return;
      }

      setSubmitting(true);
      setError("");

      try {
         let accountId = null;
         if (isInstructor) {
            const createdInstructor = await dispatch(
               addInstructor({
                  firstName: form.firstName.trim(),
                  lastName: form.lastName.trim(),
                  phone: form.phone.trim(),
                  email: form.email.trim(),
                  password: form.password,
                  // instructorul de schimb n-are sector, mașină și cutie
                  sector: hasCar ? form.sector.trim() : "",
                  color: colorIndexToCss(form.colorIndex),
                  instructorType: form.instructorType,
                  isExamen: form.instructorType === "EXAMEN",
                  isActive: true,
               }),
            ).unwrap();

            const createdInstructorId = Number(createdInstructor?.id);
            accountId = Number(createdInstructor?.userId ?? createdInstructor?.user?.id) || null;

            if (hasCar && createdInstructorId > 0 && normalizedCarPlate) {
               await upsertCarForInstructor({
                  instructorId: createdInstructorId,
                  plate: form.carPlate,
                  gearbox: form.gearbox,
               });
            }
         } else {
            const created = await createUser({
               firstName: form.firstName.trim(),
               lastName: form.lastName.trim(),
               phone: form.phone.trim(),
               email: form.email.trim(),
               password: form.password,
               role: selectedRole,
            });
            accountId = Number(created?.id) || null;
            if (supportsAccountColor && accountId) {
               await updateUser(accountId, { color: form.colorToken }).catch(() => null);
            }
         }

         // fotografia aleasă: pe contul nou (o greșeală aici nu oprește crearea)
         if (photo.blob && accountId && canSetPhoto) {
            try {
               const res = await uploadAvatar(accountId, photo.blob);
               rememberAvatarChange(accountId, res?.avatarUrl || null);
            } catch {}
         }

         await Promise.all([
            dispatch(fetchUsers()),
            ...(isInstructor ? [dispatch(fetchInstructors()), dispatch(fetchCars())] : []),
         ]);

         closePopupUI();
      } catch (submissionError) {
         setError(
            submissionError?.message ||
               "Nu am putut salva noul element de personal.",
         );
      } finally {
         setSubmitting(false);
      }
   };

   const typedName = `${form.firstName} ${form.lastName}`.trim();

   return (
      <section className="personalCreateUI staffProfilePopup">
         <div className="personalCreateUI__header popupPanelUI__header staffProfilePopup__header">
            <h3 className="personalCreateUI__title">{selectedRoleLabel} nou</h3>
         </div>

         <form className="staffProfileForm staffProfilePopup__body" onSubmit={handleSubmit}>
            {/* sus, omul așa cum va arăta: numele scris, titlul, culoarea; avatarul alege fotografia */}
            <StaffProfileHead
               person={{ firstName: form.firstName, lastName: form.lastName }}
               color={headColor}
               name={typedName || `${selectedRoleLabel} nou`}
               subtitle={getStaffTitle(selectedRole, form.instructorType)}
               photo={
                  canSetPhoto
                     ? {
                          mode: "local",
                          previewUrl: photo.previewUrl,
                          onPick: (blob, previewUrl) => setPhoto({ blob, previewUrl }),
                          onClear: () => setPhoto({ blob: null, previewUrl: "" }),
                       }
                     : null
               }
            />

            <InputUI.Group columns={2}>
               <InputUI label="Prenume" name="firstName" value={form.firstName} onChange={handleChange} placeholder="Prenume" autoComplete="off" />
               <InputUI label="Nume" name="lastName" value={form.lastName} onChange={handleChange} placeholder="Nume" autoComplete="off" />
               <InputUI label="Telefon" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="Telefon" autoComplete="off" />
               <InputUI label="Email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email" autoComplete="off" />
               <InputUI label="Parolă" name="password" type="password" value={form.password} onChange={handleChange} placeholder="Minim 6 caractere" autoComplete="new-password" />
               <InputUI label="Confirmă parola" name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} placeholder="Parola din nou" autoComplete="new-password" />

               {isInstructor ? (
                  <InputUI as="text" full label="Tip" className="staffProfileForm__choice">
                     <ChoiceUI
                        look="segmented"
                        aria-label="Tipul instructorului"
                        value={form.instructorType}
                        options={INSTRUCTOR_TYPE_OPTIONS}
                        disabled={submitting}
                        onChange={(value) => updateFormValue("instructorType", value)}
                     />
                  </InputUI>
               ) : null}

               {hasCar ? (
                  <>
                     <InputUI label="Sector" name="sector" value={form.sector} onChange={handleChange} placeholder="Sector" list="personal-create-districts" />
                     <InputUI label="Nr. mașină" name="carPlate" value={form.carPlate} onChange={handleChange} placeholder="Nr. mașină" />
                     <InputUI as="text" full label="Cutie" className="staffProfileForm__choice">
                        <ChoiceUI
                           look="segmented"
                           aria-label="Cutia de viteze"
                           value={form.gearbox === "automat" ? "automat" : "manual"}
                           options={GEARBOX_OPTIONS}
                           disabled={submitting}
                           onChange={(value) => updateFormValue("gearbox", value)}
                        />
                     </InputUI>
                  </>
               ) : null}

               {isInstructor ? (
                  <InputUI as="text" full label="Culoare" className="staffProfileForm__choice">
                     <ChoiceUI
                        look="swatches"
                        aria-label="Culoarea profilului"
                        value={String(form.colorIndex)}
                        options={PROFILE_COLOR_PALETTE.map((color, index) => ({
                           value: String(index),
                           label: color.token.replace(/^--/, ""),
                           title: color.token.replace(/^--/, ""),
                           color: color.visual,
                        }))}
                        disabled={submitting}
                        onChange={(value) => updateFormValue("colorIndex", Number(value))}
                     />
                  </InputUI>
               ) : null}

               {supportsAccountColor ? (
                  <InputUI as="text" full label="Culoare" className="staffProfileForm__choice">
                     <ChoiceUI
                        look="swatches"
                        columns={ACCOUNT_COLOR_TOKENS.length}
                        aria-label="Culoarea contului"
                        value={form.colorToken}
                        options={ACCOUNT_COLOR_TOKENS.map((token) => ({
                           value: token,
                           label: token.slice(2),
                           title: token.slice(2),
                           color: `var(--event-${token.slice(2)}, var(--event-default))`,
                        }))}
                        disabled={submitting}
                        onChange={(value) => updateFormValue("colorToken", value)}
                     />
                  </InputUI>
               ) : null}
            </InputUI.Group>
            {hasCar ? <DistrictsDatalist id="personal-create-districts" /> : null}

            {hasCar && duplicateCar ? (
               <p className="staffProfileForm__note">
                  {duplicateOwnerLabel
                     ? `Mașina ${duplicateCar.plateNumber} e la ${duplicateOwnerLabel}: trece la instructorul nou.`
                     : `Mașina ${duplicateCar.plateNumber} există deja: se leagă de instructorul nou.`}
               </p>
            ) : null}

            {error ? <p className="staffProfileForm__error">{error}</p> : null}

            <ButtonUI.Row>
               <ButtonUI onClick={closePopupUI} disabled={submitting}>
                  Renunță
               </ButtonUI>
               <ButtonUI type="submit" variant="primary" disabled={submitting} loading={submitting}>
                  {submitting ? "Se creează..." : "Creează"}
               </ButtonUI>
            </ButtonUI.Row>
         </form>
      </section>
   );
}
